import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogContent,
  Spinner,
} from '@design-system';
import { X, CirclePlay } from 'lucide-react';
import { SendResultsLinkModal, PlayIcon } from '@client';
import { QuickPlanCard } from '@/features/client-quick-plan';
import { useCancellationModal } from '../hooks/use-cancellation-modal';
import { CancellationFeedbackModal } from './cancellation-feedback-modal';

interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  //Prop drilling as useSubscrtionseats hook is re-rendering the entire subscription overview card and causing the cancellation modal to re-render and lose its state.
  refetchSubscriptionSeats?: () => void;
}

const accessLinkSuccessMessage =
  'Access link successfully sent! Now you can create their funding plan to help them achieve their financial goals.';

export function CancellationModal({
  isOpen,
  onClose,
  refetchSubscriptionSeats,
}: CancellationModalProps) {
  const {
    advisorId,
    isLoading,
    flowConfig,
    isSendResultsModalOpen,
    selectedClient,
    handleCloseSendResultsModal,
    handlePrimaryAction,
    isFeedbackModalOpen,
    handleOpenFeedbackModal,
    handleCloseFeedbackModal,
    isQuickPlanModalOpen,
    handleCloseQuickPlanModal,
    handleQuickPlanSuccessModal,
  } = useCancellationModal({ onClose });

  return (
    <>
      <AlertDialog
        open={isOpen}
        onOpenChange={onClose}
      >
        <AlertDialogContent className="sm:max-w-2xl">
          {isLoading ? (
            <div className="flex min-h-[300px] items-center justify-center">
              <Spinner className="size-8 text-gray-500" />
            </div>
          ) : (
            <>
              <button
                onClick={onClose}
                className="absolute right-4 top-4 rounded-full p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>

              <AlertDialogHeader>
                <AlertDialogTitle className="pr-8 text-2xl">{flowConfig.headline}</AlertDialogTitle>
                <AlertDialogDescription className="space-y-4 pt-2 text-left">
                  {flowConfig.body.split('\n\n').map((paragraph, index) => (
                    <p
                      key={index}
                      className="whitespace-pre-line text-[15px] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  ))}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter className="flex-col gap-3 sm:flex-row sm:justify-between">
                <AlertDialogCancel
                  onClick={handleOpenFeedbackModal}
                  className="border-none text-gray-700"
                >
                  Continue to Cancel
                </AlertDialogCancel>
                <div className="flex gap-3">
                  {flowConfig.explainerVideoUrl && (
                    <div className="border-primary bg-primary hover:bg-primary/90 cursor-pointer rounded-md border px-3 py-2 transition-colors">
                      <PlayIcon
                        id="cancellation-explainer-video"
                        icon={CirclePlay}
                        iconColor="white"
                        videoUrl={flowConfig.explainerVideoUrl}
                        size={20}
                        label="Show Me How"
                      />
                    </div>
                  )}
                  <AlertDialogAction
                    onClick={handlePrimaryAction}
                    className="min-w-[160px]"
                  >
                    {flowConfig.primaryCta}
                  </AlertDialogAction>
                </div>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {selectedClient && (
        <SendResultsLinkModal
          client={selectedClient}
          open={isSendResultsModalOpen}
          onClose={handleCloseSendResultsModal}
          successMessage={accessLinkSuccessMessage}
        />
      )}

      <CancellationFeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        onProceedToCancel={() => {
          onClose();
          refetchSubscriptionSeats?.();
        }}
      />

      <Dialog
        open={isQuickPlanModalOpen}
        onOpenChange={handleCloseQuickPlanModal}
      >
        <DialogContent>
          <QuickPlanCard
            advisorId={advisorId}
            title="Quick Plan"
            description="Generate a plan instantly by entering a few details yourself."
            onSubmitSuccess={handleQuickPlanSuccessModal}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
